import { memo, useCallback, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { MetadataKeyValueInput } from '../components';
import { AUTO_CREATE_NAMESPACE_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import {
  AutoCreateNamespaceConfig,
  DEFAULT_METADATA,
  KubernetesMetadata,
  MetadataField,
  MetadataKeyValuePair,
} from '../types';
import { metadataInputErrors } from '../validation';

export interface AutoNamespaceSectionProps {
  value: AutoCreateNamespaceConfig;
  onChange: (value: AutoCreateNamespaceConfig) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

const AutoNamespaceSectionInner = ({
  value,
  onChange,
  errors,
  enableAppSetSupport = false,
}: AutoNamespaceSectionProps) => {
  const { classes } = useStyles();
  const savedMetadataRef = useRef<KubernetesMetadata>(DEFAULT_METADATA);
  const sectionRef = useRef<HTMLDivElement>(null);
  const disabled = !value?.enabled;
  const metadataError =
    errors.length > 0
      ? metadataInputErrors(
          value?.managedNamespaceMetadata ?? DEFAULT_METADATA,
          {
            enableAppSetSupport,
          },
        )
      : '';
  // Edit mode casts `entity.spec.syncPolicy.autoCreateNamespace` straight into
  // this shape, so an application stored before the field existed arrives with
  // no `managedNamespaceMetadata` at all. Without the fallback the render below
  // dereferences `metadata.annotations` and white-screens the scaffolder page.
  const metadata = value?.managedNamespaceMetadata ?? DEFAULT_METADATA;

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (metadataError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [metadataError]);

  const handleAutoCreateNamespaceEnabledChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChangeRef.current({
          enabled: checked,
          managedNamespaceMetadata: {
            annotations: savedMetadataRef.current.annotations,
            labels: savedMetadataRef.current.labels,
          },
        });
      } else {
        const m = metadataRef.current;
        savedMetadataRef.current = {
          annotations: m.annotations,
          labels: m.labels,
        };
        onChangeRef.current({
          enabled: checked,
          managedNamespaceMetadata: { annotations: [], labels: [] },
        });
      }
    },
    [],
  );

  const handleAddField = useCallback((field: MetadataField) => {
    const v = valueRef.current;
    const m = metadataRef.current;
    onChangeRef.current({
      ...v,
      managedNamespaceMetadata: {
        ...m,
        [field]: [...(m[field] || []), { key: '', value: '' }],
      },
    });
  }, []);

  const handleRemoveField = useCallback(
    (field: MetadataField, index: number) => {
      const v = valueRef.current;
      const m = metadataRef.current;
      onChangeRef.current({
        ...v,
        managedNamespaceMetadata: {
          ...m,
          [field]: (m[field] || []).filter((_, i) => i !== index),
        },
      });
    },
    [],
  );

  const handleChangeField = useCallback(
    (
      field: MetadataField,
      index: number,
      property: keyof MetadataKeyValuePair,
      fieldValue: string,
    ) => {
      const v = valueRef.current;
      const m = metadataRef.current;
      onChangeRef.current({
        ...v,
        managedNamespaceMetadata: {
          ...m,
          [field]: (m[field] || []).map((item, i) =>
            i === index ? { ...item, [property]: fieldValue } : item,
          ),
        },
      });
    },
    [],
  );

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Automatic Namespace Creation"
          description="Configure automatic creation of the application's destination namespace."
          variant="h5"
          divider
          infoDoc={AUTO_CREATE_NAMESPACE_INFO}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Enable automatic namespace creation"
            description="Enable or disable automatic creation of the application's destination namespace."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value?.enabled)}
                color="secondary"
								onChange={e =>
                  handleAutoCreateNamespaceEnabledChange(e.target.checked)
                }
              />
            }
            label="Enable automatic namespace creation"
          />
        </Box>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Annotations"
            description="Manage annotations for the application's destination namespace."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="annotation"
          items={metadata.annotations || []}
          disabled={disabled}
          onAdd={() => handleAddField('annotations')}
          onRemove={index => handleRemoveField('annotations', index)}
          onChange={(index, property, val) =>
            handleChangeField('annotations', index, property, val)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Labels"
            description="Manage labels for the application's destination namespace."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="label"
          items={metadata.labels || []}
          disabled={disabled}
          onAdd={() => handleAddField('labels')}
          onRemove={index => handleRemoveField('labels', index)}
          onChange={(index, property, val) =>
            handleChangeField('labels', index, property, val)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        {metadataError && (
          <Box className={classes.inputContainer}>
            <ScaffolderFieldError message={metadataError} />
          </Box>
        )}
      </ScaffolderFormPaper>
    </div>
  );
};

export const AutoNamespaceSection = memo(AutoNamespaceSectionInner);
